import { Minus, Plus, Maximize } from 'lucide-react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { EditorCanvasHandle } from './editor-canvas.js';

interface ZoomControlProps {
  zoomLevel: number;
  canvasHandleRef: RefObject<EditorCanvasHandle | null> | undefined;
  disabled?: boolean;
}

const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4] as const;

/**
 * Bottom-right zoom widget. Compact horizontal pill — minus / value / plus —
 * with a fit-to-view button glued on the right. Replaces the percent label
 * and Maximize button that used to live at the bottom of the toolbar.
 */
export function ZoomControl({ zoomLevel, canvasHandleRef, disabled = false }: ZoomControlProps) {
  const { t } = useTranslation('editor');
  const handle = canvasHandleRef?.current;

  const stepTo = (direction: 'in' | 'out') => {
    if (!handle) return;
    const current = handle.getViewport().scale;
    let next: number;
    if (direction === 'in') {
      next = ZOOM_STEPS.find((s) => s > current + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]!;
    } else {
      const reversed = [...ZOOM_STEPS].reverse();
      next = reversed.find((s) => s < current - 0.001) ?? ZOOM_STEPS[0]!;
    }
    handle.setZoom(next);
  };

  const zoomPercent = Math.round(zoomLevel * 100);
  const buttonStyle = {
    appearance: 'none' as const,
    background: 'transparent',
    border: 0,
    color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
    width: '30px',
    height: '30px',
    display: 'grid',
    placeItems: 'center',
    cursor: disabled ? 'default' : 'pointer',
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '76px',
        right: '24px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        backgroundColor: 'rgba(18, 18, 22, 0.45)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.07), 0 8px 24px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        zIndex: 19,
      }}
      data-testid="zoom-control"
    >
      <button
        type="button"
        onClick={() => stepTo('out')}
        disabled={disabled}
        aria-label={t('toolbar.zoomOut', { defaultValue: 'Zoom out' })}
        title={t('toolbar.zoomOut', { defaultValue: 'Zoom out' })}
        style={buttonStyle}
      >
        <Minus size={12} aria-hidden="true" />
      </button>
      <span
        aria-label={t('toolbar.zoomLevel', { percent: zoomPercent })}
        data-testid="zoom-display"
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '11px',
          fontWeight: 500,
          color: 'var(--color-text-secondary)',
          padding: '0 12px',
          minWidth: '56px',
          textAlign: 'center',
          borderLeft: '1px solid var(--color-border)',
          borderRight: '1px solid var(--color-border)',
          height: '30px',
          display: 'grid',
          placeItems: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {zoomPercent}%
      </span>
      <button
        type="button"
        onClick={() => stepTo('in')}
        disabled={disabled}
        aria-label={t('toolbar.zoomIn', { defaultValue: 'Zoom in' })}
        title={t('toolbar.zoomIn', { defaultValue: 'Zoom in' })}
        style={buttonStyle}
      >
        <Plus size={12} aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => handle?.fitToView()}
        disabled={disabled}
        aria-label={t('toolbar.fitToView')}
        title={t('toolbar.fitToView')}
        style={{
          ...buttonStyle,
          borderLeft: '1px solid var(--color-border)',
        }}
      >
        <Maximize size={12} aria-hidden="true" />
      </button>
    </div>
  );
}
